import { forkJoin, Subscription } from "rxjs";

import { map } from "rxjs/operators";
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import {
  ChartType,
  ChartTypeResponse,
  ChartTypeService,
  DropdownService,
  ListOfQuestions,
  Question,
  QuestionService,
  Source,
  SourceService,
  SubcategoryService,
  SubGroupOrder,
  SubgroupOrdersResponse,
  SubgroupService,
  TaxReturnService,
  ChartTypeConfigInfo
} from "../../../../../swaggerServices";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import {
  IFormField,
  makeControlForQuestion
} from "../../../../shared/components/form-field-wrapper/form-field-wrapper.component";
import { cloneDeep } from "lodash-es";
import { QuestionsResolver } from "../questions-resolver";
import {
  DropdownListOption,
  IListOption,
  SearchDropdownComponent
} from "../../../../shared/components/search-dropdown/search-dropdown.component";
import { ConfigStateService } from "../../../config/config-state.service";
import { environment } from "../../../../environments/environment";
import { TenantConfigService } from "../../../../shared/services/tenant-config.service";
import { TranslocoService } from "@jsverse/transloco";

declare var $: any;

export interface IQuestionWithScAndSg extends IFormField {
  subCategory?: {
    id: number;
    questions: IFormField[];
  };
  subGroup?: {
    id: number;
    questions: IFormField[];
  };
  questionDropdown?: IListOption[];
  APSDimension?: number;
  APSOrder?: number;
  APSCalcField?: boolean;
  tacRef?: string;
  lodgementBotField?: string;
  taxReturnTypeId?: number;
  answersCount?: number;
  lockMode?: string;
}

export interface IQuestionWithStatus extends IQuestionWithScAndSg {
  status?: number;
}

@Component({
    selector: "app-question",
    templateUrl: "./question.component.html",
    styleUrls: ["./question.component.scss"],
    standalone: false
})
export class QuestionComponent implements OnInit, OnDestroy {
  question: IQuestionWithScAndSg;
  originalQuestion: IQuestionWithScAndSg;
  questions: Question[];
  sources: Source[];
  formGroup: UntypedFormGroup;
  prevFormGroup: UntypedFormGroup;
  dependanciesFormGroup: UntypedFormGroup;
  sourceFormGroup: UntypedFormGroup;
  @ViewChild("questionTypeDropDown")
  questionTypeDropDown: ElementRef;
  @ViewChild("defaultValueDropdown")
  defaultValueDropdown: ElementRef;
  @ViewChild("numberOptionsDropDown")
  numberOptionsDropDown: ElementRef;
  @ViewChild("depMandQuestionDropDown")
  depMandQuestionDropDown: ElementRef;
  @ViewChild("depMandAnswerDropdown")
  depMandAnswerDropdown: ElementRef;
  @ViewChild("sourceDropdown")
  sourceDropdown: ElementRef;
  @ViewChild("addQuestionModal")
  addQuestionModal: ElementRef;
  @ViewChild("newQuestionModal")
  newQuestionModal: ElementRef;
  showNewQuestionModal: boolean;
  dependencyType: string;
  dependantMandatoryQuestion: IFormField;
  tab: number = 1;
  clickedRow: boolean;
  questionType: UntypedFormControl;
  isTab: UntypedFormControl;
  hidden: UntypedFormControl;
  hiddenForPreparer: UntypedFormControl;
  complexityOverride: UntypedFormControl;
  topLevel: UntypedFormControl;
  lockMode: UntypedFormControl;
  lockType: UntypedFormControl;

  questionName: UntypedFormControl;
  mandatory: UntypedFormControl;
  default: UntypedFormControl;
  min: UntypedFormControl;
  max: UntypedFormControl;
  maxLength: UntypedFormControl;
  valueFormat: UntypedFormControl;
  allowDecimals: UntypedFormControl;
  hint: UntypedFormControl;
  APSRef: UntypedFormControl;
  APSSection: UntypedFormControl;
  APSSubsection: UntypedFormControl;
  APSQuestionGrouping: UntypedFormControl;
  APSDimension: UntypedFormControl;
  APSOrder: UntypedFormControl;
  APSCalcField: UntypedFormControl;
  tacRef: UntypedFormControl;
  isTable: UntypedFormControl;
  medicareCoordinates: UntypedFormControl;
  customFilePath: UntypedFormControl;
  sharepointTags: UntypedFormControl;
  transmittalLetterRef: UntypedFormControl;
  tag: UntypedFormControl;
  taxServiceIdentifier: UntypedFormControl;
  isManualAPSEntry: UntypedFormControl;
  lodgementBotField: UntypedFormControl;
  alwaysEditable: UntypedFormControl;
  questionSearch: string;
  columnQuestions: IFormField[] = []; // ids of questions which need to be saved with parentId on save
  isSaving: boolean;
  isSyncingCache: boolean;
  subscriptions: Subscription[] = [];
  canUpdateTaxServiceIdentifier: boolean;
  canUpdateManualAPSEntry: boolean;
  showDelete: boolean;
  isDeletedQuestion: boolean;
  @ViewChild(SearchDropdownComponent)
  chartTypeDropDown: SearchDropdownComponent;
  isDisplayAsChart: UntypedFormControl;
  chartTypeFormCtrl: UntypedFormControl;
  sourceId: UntypedFormControl;
  chartDisplayFieldType: string;
  chartConfigRequirements = [];
  chartOptions: Array<ChartType> = [];
  displayAsChartOptions: Array<any> = [
    { name: "Display as Chart", value: "true" }
  ];
  chartRequiredColumnWarningMessage: String;
  chartRequiredColumnWarning = false;
  requiedNumColumns: number;
  isProductionMode: boolean;
  uiConfig: TenantConfigService;
  yearLabel: string;
  tempValueDropdownId: number = -1;

  customFilePathRegex = /^(\.\\|\.\.\\)(?!(\\|\.\.\.))+|^(?!(\\|\.))+/;
  questionHasAnswerText: string;

  constructor(
    private activatedRoute: ActivatedRoute,
    private questionService: QuestionService,
    private questionsResolver: QuestionsResolver,
    private router: Router,
    private taxReturnService: TaxReturnService,
    private sourceService: SourceService,
    private subcategoryService: SubcategoryService,
    private subgroupService: SubgroupService,
    private dropdownService: DropdownService,
    private chartTypeService: ChartTypeService,
    public config: ConfigStateService,
    private translocoService: TranslocoService
  ) {
    this.isProductionMode = environment.production;
  }

  getQuestionsByyear() {
    this.subscriptions.push(
      this.questionService
        .getQuestionsByyear(this.question.year)
        .subscribe((q: ListOfQuestions) => {
          this.questions = q.questions;
          this.initDepQuestionDropdown(q.questions);
          this.initTableQuestions();

          // this can only be set as true for 1 question
          const qWithIsManualAPSEntry = this.questions.find(
            _q => _q.isManualAPSEntry
          );
          if (qWithIsManualAPSEntry)
            this.canUpdateManualAPSEntry =
              qWithIsManualAPSEntry.id === this.question.id;
          else this.canUpdateManualAPSEntry = true;

          const qWithTaxServiceIdentifier = this.questions.find(
            _q => _q.taxServiceIdentifier
          );
          if (qWithTaxServiceIdentifier)
            this.canUpdateTaxServiceIdentifier =
              qWithTaxServiceIdentifier.id === this.question.id;
          else this.canUpdateTaxServiceIdentifier = true;
        })
    );
  }

  // find all question table's column questions
  initTableQuestions() {
    this.columnQuestions = [];
    this.questions.forEach(q => {
      if (q.parentId === this.question.id) this.columnQuestions.push(q);
    });
    this.updateTableColumnOrder();
  }

  initSources(s) {
    this.sources = s;
    this.initSourceDropdown(s);
  }
  ngOnInit() {
    this.subscriptions.push(
      this.questionsResolver.getQuestionsObs().subscribe((q: Question) => {
        this.initQuestion(q);
        const questionWithStatus: IQuestionWithStatus = q;
        questionWithStatus.status === 99
          ? (this.isDeletedQuestion = true)
          : (this.isDeletedQuestion = false);
      })
    );
    this.subscriptions.push(
      this.questionsResolver
        .getQuestionsObs()
        .subscribe((q: Question) => this.initQuestion(q))
    );
    this.subscriptions.push(
      this.config.loadConfig().subscribe(config => {
        this.uiConfig = new TenantConfigService(config);
        this.yearLabel = this.uiConfig.config.yearLabel;
        this.translocoService
          .selectTranslate("year")
          .subscribe(value => (this.yearLabel = value));
        this.translocoService
          .selectTranslate("question-has-answer")
          .subscribe(value => (this.questionHasAnswerText = value));
        this.showDelete = config.featureFlags.allowSoftDelete;
      })
    );
  }

  unfocusValueRows() {
    if (!this.question.questionDropdowns) return;
    this.question.questionDropdowns.forEach(
      (v: IListOption) => (v.edit = false)
    );
  }

  getQuestionType(q: IFormField): string {
    let questionType = q.displayFieldType;
    if (q.displayFieldType === "text") {
      if (q.dataType === "string") {
        questionType = "text";
      } else if (
        q.dataType.toLowerCase() === "integer" ||
        q.dataType.toLowerCase() === "float"
      ) {
        questionType = "number";
      }
    }
    if (q.dataType === "table") {
      questionType = "table";
    }
    return questionType;
  }

  initQuestion(q: IFormField) {
    if (!q.questionDropdowns) q.questionDropdowns = [];
    q.questionDropdowns = q.questionDropdowns.sort((a, b) => a.order - b.order); // order dropdown values by selected order

    this.originalQuestion = cloneDeep(q);
    this.dependencyType =
      q.dependantMandatoryParentValue && q.dependantMandatoryParentId
        ? "specific"
        : "any";

    // answers is req by field-wrapper
    q.answers = [];
    this.question = q;
    // for table questions, always refresh questions list
    if (this.question.dataType === "table") {
      this.getQuestionsByyear();
    } else {
      if (!this.questions || this.questions.length === 0)
        this.getQuestionsByyear();
    }

    // init forms
    this.formGroup = new UntypedFormGroup({});
    this.prevFormGroup = new UntypedFormGroup({});
    this.dependanciesFormGroup = new UntypedFormGroup({});
    this.sourceFormGroup = new UntypedFormGroup({});
    this.questionType = new UntypedFormControl(this.getQuestionType(q));
    this.isTab = new UntypedFormControl(this.question.isTab || false);
    this.hidden = new UntypedFormControl(this.question.hidden || false);
    this.hiddenForPreparer = new UntypedFormControl(
      this.question.hiddenForPreparer || false
    );
    this.complexityOverride = new UntypedFormControl(
      this.question.complexityOverride || false
    );
    this.topLevel = new UntypedFormControl(this.question.topLevel || false);
    this.questionName = new UntypedFormControl(this.question.question);
    this.mandatory = new UntypedFormControl(this.question.mandatory);
    this.lockMode = new UntypedFormControl(this.question.lockMode || 'auto');
    this.lockType = new UntypedFormControl(this.question.lockType || 'manualInput');

    this.default = new UntypedFormControl(this.question.default, {
      validators: Validators.compose([Validators.maxLength(255)])
    });
    this.min = new UntypedFormControl(this.question.min);
    this.max = new UntypedFormControl(this.question.max);
    this.maxLength = new UntypedFormControl(this.question.maxLength);
    this.hint = new UntypedFormControl(this.question.hint);
    this.APSRef = new UntypedFormControl(this.question.APSRef);
    this.APSSection = new UntypedFormControl(this.question.APSSection);
    this.APSSubsection = new UntypedFormControl(this.question.APSSubsection);
    this.APSQuestionGrouping = new UntypedFormControl(
      this.question.APSQuestionGrouping
    );
    this.APSDimension = new UntypedFormControl(this.question.APSDimension);
    this.APSOrder = new UntypedFormControl(this.question.APSOrder);
    this.APSCalcField = new UntypedFormControl(this.question.APSCalcField || false);
    this.tacRef = new UntypedFormControl(this.question.tacRef);
    this.valueFormat = new UntypedFormControl(this.question.valueFormat);
    this.allowDecimals = new UntypedFormControl();
    this.isTable = new UntypedFormControl(this.question.dataType === "table");
    this.medicareCoordinates = new UntypedFormControl(
      this.question.medicareCoordinates
    );
    this.transmittalLetterRef = new UntypedFormControl(
      this.question.transmittalLetterRef
    );
    this.tag = new UntypedFormControl(this.question.tag);
    this.taxServiceIdentifier = new UntypedFormControl(
      this.question.taxServiceIdentifier || false
    );
    this.isManualAPSEntry = new UntypedFormControl(
      this.question.isManualAPSEntry || false
    );
    this.lodgementBotField = new UntypedFormControl(this.question.lodgementBotField);
    this.alwaysEditable = new UntypedFormControl(
      this.question.alwaysEditable || false
    );
    this.sourceId = new UntypedFormControl(this.question.sourceId);
    this.customFilePath = new UntypedFormControl(this.question.customFilePath, {
      validators: Validators.compose([
        Validators.pattern(this.customFilePathRegex)
      ])
    });
    this.sharepointTags = new UntypedFormControl(this.question.sharepointTags);
    this.isDisplayAsChart = new UntypedFormControl();
    this.isDisplayAsChart.setValue(false);
    this.chartTypeFormCtrl = new UntypedFormControl();

    this.formGroup.addControl("questionType", this.questionType);
    this.formGroup.addControl("isTab", this.isTab);
    this.formGroup.addControl("hidden", this.hidden);
    this.formGroup.addControl("hiddenForPreparer", this.hiddenForPreparer);
    this.formGroup.addControl("complexityOverride", this.complexityOverride);
    this.formGroup.addControl("topLevel", this.topLevel);
    this.formGroup.addControl("questionName", this.questionName);
    this.formGroup.addControl("mandatory", this.mandatory);
    this.formGroup.addControl("lockMode",  this.lockMode );
    this.formGroup.addControl("lockType", this.lockType);
    this.formGroup.addControl("default", this.default);
    this.formGroup.addControl("maxLength", this.maxLength);
    this.formGroup.addControl("hint", this.hint);
    this.formGroup.addControl("min", this.min);
    this.formGroup.addControl("max", this.max);
    this.formGroup.addControl("APSRef", this.APSRef);
    this.formGroup.addControl("APSSection", this.APSSection);
    this.formGroup.addControl("APSSubsection", this.APSSubsection);
    this.formGroup.addControl("APSQuestionGrouping", this.APSQuestionGrouping);
    this.formGroup.addControl("APSDimension", this.APSDimension);
    this.formGroup.addControl("APSOrder", this.APSOrder);
    this.formGroup.addControl("APSCalcField", this.APSCalcField);
    this.formGroup.addControl("tacRef", this.tacRef);
    this.formGroup.addControl("valueFormat", this.valueFormat);
    this.formGroup.addControl("allowDecimals", this.allowDecimals);
    this.formGroup.addControl("isTable", this.isTable);
    this.formGroup.addControl("displayFieldType", new UntypedFormControl());
    this.formGroup.addControl("medicareCoordinates", this.medicareCoordinates);
    this.formGroup.addControl("customFilePath", this.customFilePath);
    this.formGroup.addControl("sharepointTags", this.sharepointTags);
    this.formGroup.addControl(
      "transmittalLetterRef",
      this.transmittalLetterRef
    );
    this.formGroup.addControl("tag", this.tag);
    this.formGroup.addControl(
      "taxServiceIdentifier",
      this.taxServiceIdentifier
    );
    this.formGroup.addControl("isManualAPSEntry", this.isManualAPSEntry);
    this.formGroup.addControl("lodgementBotField", this.lodgementBotField);
    this.formGroup.addControl("alwaysEditable", this.alwaysEditable);
    this.formGroup.addControl("chartTypeFormCtrl", this.chartTypeFormCtrl);
    this.initTableChartTypeDropdown();

    // dependencies tab form group
    const dependantMandatoryParentId = new UntypedFormControl();
    this.dependanciesFormGroup.addControl(
      "dependantMandatoryParentId",
      dependantMandatoryParentId
    );

    // sources tab form group
    this.sourceFormGroup.addControl("sourceId", this.sourceId);
    this.subscriptions.push(
      this.sourceService.getSources().subscribe(s => this.initSources(s))
    );

    if (this.question.dataType === "Float") this.allowDecimals.setValue(true);
    else if (this.question.dataType === "integer")
      this.allowDecimals.setValue(false);

    this.formGroup.valueChanges.subscribe(formGroup => {
      Object.keys(formGroup).forEach(
        key => (this.question[key] = formGroup[key])
      );

      this.question.question = formGroup.questionName;
      this.question.mandatory = Boolean(formGroup.mandatory);

      if (formGroup.questionType === "number") {
        this.question.displayFieldType = "text";
        if (this.allowDecimals.value) {
          this.question.dataType = "Float";
        } else {
          this.question.dataType = "integer";
        }
      } else {
        this.question.displayFieldType = formGroup.questionType;
        this.question.dataType = "string";
        this.min.setValue(null, {
          emitEvent: false
        });
        this.max.setValue(null, {
          emitEvent: false
        });
        this.question.min = null;
        this.question.max = null;
      }

      if (formGroup.questionType === "checkbox" && formGroup.mandatory) {
        this.question.mandatory = false;
        this.mandatory.setValue("false", {
          emitEvent: false
        });
      }

      // table q cannot not also be a tab q
      if (formGroup.questionType === "table") {
        this.question.dataType = "table";

        this.isTab.setValue(false, {
          emitEvent: false
        });
        this.mandatory.setValue(false, {
          emitEvent: false
        });
        this.isTable.setValue(true, {
          emitEvent: false
        });
      } else {
        this.isTable.setValue(false, {
          emitEvent: false
        });
      }

      // update preview
      this.updatePreview();

      // need to re-init ui controls on values and table tab on question type change
      $(".ui.checkbox").checkbox();
    });

    // init all ui controls
    setTimeout(() => {
      this.initQuestionTypeDropdown();
      this.initDefaultValueDropdown();

      const values = [
        {
          name: "Select...",
          value: null,
          selected: !this.question.isCurrency && !this.question.isPercentage
        },
        {
          name: "Currency",
          value: "currency",
          selected: this.question.isCurrency
        },
        {
          name: "Percentage",
          value: "percentage",
          selected: this.question.isPercentage
        }
      ];

      $(this.numberOptionsDropDown.nativeElement).dropdown({
        placeholder: "Select...",
        fullTextSearch: "exact",
        preserveHTML: false,
        values: values,
        onChange: (value: string) => {
          this.question.isCurrency = value === "currency";
          this.question.isPercentage = value === "percentage";
        }
      });

      $("#page").click(() =>
        !this.clickedRow ? this.unfocusValueRows() : undefined
      );

      setTimeout(() => {
        $(".ui.checkbox").checkbox();
        $("#table.tab .new-value-btn").popup({
          on: "click",
          position: "right center"
        });
      });
      this.updatePreview();
    });
  }

  // re-init ui controls for tabs is required when question type changes
  goToTab(tabNum: number) {
    this.tab = tabNum;
  }

  // update question type dropdown to set table question
  updateQuestionType() {
    this.questionType.setValue("table", {
      emitEvent: false
    });
    this.initQuestionTypeDropdown();
  }

  // when clicking 'no', revert question type back to it's previous
  revertQuestionType() {
    this.question.displayFieldType = this.originalQuestion.displayFieldType;

    // determine question type
    if (this.getQuestionType(this.originalQuestion) === "number") {
      this.questionType.setValue("number", {
        emitEvent: false
      });
    } else {
      this.questionType.setValue(this.originalQuestion.displayFieldType, {
        emitEvent: false
      });
    }

    this.initQuestionTypeDropdown();
  }

  initQuestionTypeDropdown() {
    const valuesOptions: {}[] = [
      {
        value: "text",
        name: "Text",
        selected: this.questionType.value === "text"
      },
      {
        value: "number",
        name: "Number",
        selected: this.questionType.value === "number"
      },
      {
        value: "textarea",
        name: "Textarea",
        selected: this.questionType.value === "textarea"
      },
      {
        value: "drop down",
        name: "Drop Down",
        selected: this.questionType.value === "drop down"
      },
      {
        value: "radio",
        name: "Radio",
        selected: this.questionType.value === "radio"
      },
      {
        value: "date picker",
        name: "Date Picker",
        selected: this.questionType.value === "date picker"
      },
      {
        value: "checkbox",
        name: "Checkbox",
        selected: this.questionType.value === "checkbox"
      },
      // {
      //   value: "image",
      //   name: "Image",
      //   selected: this.questionType.value === "image"
      // },
      {
        value: "file",
        name: "File",
        selected: this.questionType.value === "file"
      }
    ];
    // this.lockMode.setValue('auto');
    // this.lockType.setValue('manualInput');


    // can only set unsaved questions to table
    if (
      (!this.question.id && !this.question.isTab) ||
      (!this.question.id && this.question.dataType === "table") ||
      (this.question.id && this.question.dataType === "table")
    ) {
      valuesOptions.push({
        value: "table",
        name: "Table",
        selected: this.questionType.value === "table"
      });
    }

    $(this.questionTypeDropDown.nativeElement).dropdown({
      values: valuesOptions,
      placeholder: "Select...",
      fullTextSearch: "exact",
      preserveHTML: false,
      onChange: (value: string) => {
        if (value) this.questionType.setValue(value);
        if (value === "table") {
          this.alwaysEditable.setValue(false);
          this.chartDisplayFieldType = this.question.displayFieldType;
        }
      }
    });
  }

  onTabValueChanges() {
    if (this.isTab && this.isTab.value) {
      this.alwaysEditable.setValue(false);
    }
    if (this.questionType.value === "table") {
      this.isTab.setValue(false, {
        emitEvent: false
      });
      this.question.isTab = false;
    }
  }

  initDefaultValueDropdown() {
    if (
      !this.question.questionDropdowns ||
      (this.getQuestionType(this.question) !== "drop down" &&
        this.getQuestionType(this.question) !== "radio")
    )
      return;

    const defaultValueDropdownOptions = this.question.questionDropdowns
      .filter(v => !v.disabled)
      .sort((a, b) => a.order - b.order)
      .map(v => {
        return {
          value: v.value,
          name: v.display,
          selected: v.value === this.question.default
        };
      });

    const valuesPlaceholder: {}[] = [
      {
        name: "Select...",
        order: 0
      }
    ];

    $(this.defaultValueDropdown.nativeElement).dropdown({
      values: valuesPlaceholder.concat(defaultValueDropdownOptions),
      placeholder: "Select...",
      fullTextSearch: "exact",
      preserveHTML: false,
      onChange: (value: string) => {
        value != "undefined"
          ? this.default.setValue(value)
          : this.default.setValue(null);
        // update values list to toggle default selected checkbox
        this.question.questionDropdowns.forEach(
          (v: IListOption) => (v.selected = v.value === value)
        );
      }
    });
  }

  // init the dropdown of all questions for dependant mandatory
  initDepQuestionDropdown(questions: IFormField[]) {
    this.questions = questions;

    const valuesPlaceholder: {}[] = [
      {
        name: "Select...",
        value: 0,
        order: 0
      }
    ];

    const depMandQuestionDropDownVals = questions
      .filter(
        q =>
          q.id !== this.question.id &&
          !q.hidden &&
          q.dataType !== "table" &&
          !q.parentId
      )
      .map(q => {
        return {
          value: q.id,
          name: q.question,
          selected: q.id === this.question.dependantMandatoryParentId
        };
      });

    $(this.depMandQuestionDropDown.nativeElement).dropdown({
      values: valuesPlaceholder.concat(depMandQuestionDropDownVals),
      placeholder: "Select...",
      fullTextSearch: "exact",
      preserveHTML: false,
      onChange: (value: number) => {
        if (value) {
          this.dependantMandatoryQuestion = this.questions.find(
            q => q.id === Number(value)
          );
          if (value > 0) {
            this.question.dependantMandatoryParentId = Number(value);
          } else {
            this.question.dependantMandatoryParentId = null;
            this.question.dependantMandatoryParentValue = null;
            this.dependencyType = "any";
          }
          if (
            this.dependantMandatoryQuestion &&
            (this.dependantMandatoryQuestion.displayFieldType === "drop down" ||
              this.dependantMandatoryQuestion.displayFieldType === "radio")
          )
            this.initDepAnsDropdown();
        }
      }
    });
  }

  // get the dropdown values for the dependant question
  initDepAnsDropdown() {
    this.questionService
      .getquestionById(this.dependantMandatoryQuestion.id)
      .subscribe((q: IFormField) => {
        const depMandAnswerDropdownVals = q.questionDropdowns.map(q => {
          return {
            value: q.value,
            name: q.display,
            selected: q.value === this.question.dependantMandatoryParentValue
          };
        });

        $(this.depMandAnswerDropdown.nativeElement).dropdown({
          values: depMandAnswerDropdownVals,
          placeholder: "Select...",
          fullTextSearch: "exact",
          preserveHTML: false,
          onChange: (value: string) => {
            if (value) {
              this.question.dependantMandatoryParentValue = value;
            }
          }
        });
      });
  }

  // init the dropdown of all questions for dependant mandatory
  initSourceDropdown(sources: Source[]) {
    this.sources = sources;

    const valuesPlaceholder: {}[] = [
      {
        name: "Select...",
        value: 0,
        order: 0
      }
    ];

    const sourceDropDownVals = sources
      .filter(s => s.requireAtlasId && s.type === "csv")
      .map(s => {
        return {
          value: s.id,
          name: s.name,
          selected: s.id === this.question.sourceId
        };
      });

    $(this.sourceDropdown.nativeElement).dropdown({
      values: valuesPlaceholder.concat(sourceDropDownVals),
      placeholder: "Select...",
      fullTextSearch: "exact",
      preserveHTML: false,
      onChange: (value: number) => {
        if (value > 0) {
          this.question.sourceId = Number(value);
          // this.sourceId.setValue(Number(value));
        } else {
          // this.sourceId.setValue(null);
          this.question.sourceId = null;
        }
      }
    });
  }

  // checks whether question's list options already contains a default selection
  questionHasDefault() {
    return this.question.questionDropdowns.some(d => d.selected);
  }

  deleteValue(listOption: IListOption) {
    listOption.status = 99;
    this.initDefaultValueDropdown();
    this.updatePreview();
  }

  addValue() {
    this.question.questionDropdowns.unshift({
      // Note (Clint): id is temporarily set to a negative number until saved in DB
      // This is because the default checkbox is set on id and all new questions are null
      // causing the default checkbox to be selected for all new dropdown values
      id: this.tempValueDropdownId,
      value: null,
      display: null,
      order: null,
      selected: null,
      disabled: null,
      status: 0
    });

    this.tempValueDropdownId--;

    // focus first input
    setTimeout(() => {
      this.question.questionDropdowns[0]["edit"] = true;
      $("#tbl-container")
        .find("#tbl-container tbody tr:first-of-type td:first-of-type input")
        .focus();
      $(".ui.checkbox").checkbox();
      this.updatePreview();
    });
  }

  editValue(valueOption: IListOption, event) {
    this.unfocusValueRows();
    valueOption.edit = true;
    $(event.target)
      .find("input")
      .focus();
    this.clickedRow = true; // prevent unfocusValueRows() causing row to de-activate
    setTimeout(() => (this.clickedRow = false));
  }

  // only 1 option can be defaut selected. toggle all other options
  toggleDefaultValue(valueOption: IListOption) {
    const value = cloneDeep(valueOption);
    this.question.questionDropdowns.forEach((v: IListOption) => {
      v.selected = false;
      if (v.id === value.id) v.selected = !value.selected;
      if (v.selected) this.question.default = value.value;
    });
    if (!this.question.questionDropdowns.some(v => v.selected))
      this.question.default = null;
    this.initDefaultValueDropdown();
  }

  getChartType(value: string) {
    let found = null;
    if (this.chartOptions) {
      this.chartOptions.forEach((o: ChartType) => {
        if (o.value === value) {
          found = o;
        }
      });
    }
    return found;
  }

  updateTableColumnOrder() {
    this.subscriptions.push(
      this.subgroupService
        .getSubgroupOrders(this.columnQuestions.map(q => `${q.id}`))
        .subscribe((res: SubgroupOrdersResponse) => {
          this.columnQuestions.forEach(q => {
            const sg = res.subGroupOrders?.find(s => s.questionId === q.id);
            if (sg) q.subGroupOrder = { order: sg.order };
          });
          this.columnQuestions.sort(
            (a, b) => a.subGroupOrder?.order - b.subGroupOrder?.order
          );
          this.updateChartConfigRequirements();
        })
    );
  }

  updateChartConfigRequirements() {
    const chart = this.getChartType(this.chartDisplayFieldType);
    if (chart) {
      this.chartConfigRequirements = [];
      this.requiedNumColumns = chart.minColumnsRequired;
      const config: ChartTypeConfigInfo[] = chart.configInfo;

      // Ignore if isRowId == true
      const relevantQuestions = this.columnQuestions.filter(q => !q.isRowId);

      config.forEach((c: ChartTypeConfigInfo) => {
        const q = relevantQuestions[c.columnOrder]
          ? relevantQuestions[c.columnOrder]
          : null;
        let isValid = true;
        let message = "";
        let questionName = "";

        if (q) {
          questionName = q.question;
          if (
            q.dataType.toLowerCase() === "integer" ||
            q.dataType.toLowerCase() === "float"
          ) {
            isValid = c.reqDataType === "number";
            message = isValid
              ? ` will be the ${c.axis}. ${c.message}.`
              : ` is required to be a ${c.reqDataType}.`;
          } else {
            isValid = q.dataType === c.reqDataType;
            message = isValid
              ? ` will be the ${c.axis}. ${c.message}.`
              : ` is required to be a ${c.reqDataType}.`;
          }
        } else if (c.requiredColumn) {
          isValid = false;
          message = `A column is required for the ${c.axis}.`;
        } else {
          message = `An optional column is for the ${c.axis} is required for double stacked bar charts.`;
        }
        this.chartConfigRequirements.push({
          isValid: isValid,
          message: message,
          questionName: questionName
        });
      });
    } else {
      this.chartConfigRequirements = null;
      this.requiedNumColumns = 0;
    }
    this.handleChartMinColumnWarning();
  }

  handleChartMinColumnWarning() {
    const numColumns = this.columnQuestions
      ? this.columnQuestions.filter(q => q.isRowId === false).length
      : 0;
    const requiedNumColumns = this.requiedNumColumns
      ? this.requiedNumColumns
      : 0;
    if (this.isDisplayAsChart.value && numColumns < this.requiedNumColumns) {
      this.chartRequiredColumnWarningMessage = `A minimum of ${requiedNumColumns} columns are required for this chart. Only ${numColumns} valid column${
        numColumns > 1 ? "s" : ""
      } found.`;
      this.chartRequiredColumnWarning = true;
    } else {
      this.chartRequiredColumnWarning = false;
    }
  }

  initTableChartTypeDropdown() {
    this.chartDisplayFieldType = this.question.displayFieldType;
    this.subscriptions.push(
      this.chartTypeService
        .chartTypeGet()
        .subscribe((res: ChartTypeResponse) => {
          const chartTypeValues: DropdownListOption[] = res.chartTypes.map(
            (v, index, number) => {
              return {
                id: index,
                name: v.name,
                value: v.value,
                order: v.order
              };
            }
          );

          this.chartOptions = [...res.chartTypes];
          this.chartTypeDropDown.updateListOptions([...chartTypeValues]);
          const result = this.chartOptions.find(
            (o: ChartType) => o.value === this.question.displayFieldType
          );

          if (result) {
            this.chartTypeFormCtrl.setValue(result.value);
            this.chartTypeDropDown.setSelected(result.value);
            this.isDisplayAsChart.setValue(true);
            this.chartDisplayFieldType = result.value;
            this.updateChartConfigRequirements();
          }
        })
    );

    this.subscriptions.push(
      this.chartTypeFormCtrl.valueChanges.subscribe(value => {
        if (value) {
          if (value.value) this.chartDisplayFieldType = value.value;
          this.requiedNumColumns = this.getChartType(value.value)
            ? this.getChartType(value.value).minColumnsRequired
            : 0;
          this.updateChartConfigRequirements();
        }
      })
    );
  }

  updateDisplayAsChart(event) {
    event.stopPropagation();
    if (event.target.checked) {
      this.isDisplayAsChart.setValue(true);
    } else {
      this.isDisplayAsChart.setValue(false);
      this.chartTypeFormCtrl.setValue(null);
      this.chartDisplayFieldType = "table";
      this.chartTypeDropDown.clearSelection();
      this.updateChartConfigRequirements();
    }
  }

  // update question preview
  updatePreview() {
    if (!this.question) return;
    if (this.questionType.value !== "table") {
      this.prevFormGroup.removeControl(String(this.question.id));
      setTimeout(() => {
        this.prevFormGroup.addControl(
          String(this.question.id),
          makeControlForQuestion(this.question)
        );
      });
    }
  }

  addExistingQuestion() {
    $(this.addQuestionModal.nativeElement).modal("show");
    $("#table.tab .new-value-btn").popup("hide");
  }

  addNewQuestion() {
    this.showNewQuestionModal = true;
    $(this.newQuestionModal.nativeElement)
      .modal({
        onDeny: () => {
          this.showNewQuestionModal = false; // reset new question component
          $(this.newQuestionModal.nativeElement).modal("hide");
        }
      })
      .modal("show");
    $("#table.tab .new-value-btn").popup("hide");
  }

  // question can only be added to table if it is not already used, is not a table itself, is not the same question id, and is not tab question
  // question type file that is used as a source also cannot be added to a table question
  canAddTableQuestion(question: IFormField) {
    return (
      !this.columnQuestions.find(q => q.id === question.id) &&
      question.dataType !== "table" &&
      !question.parentId &&
      !question.isTab &&
      !question.sourceId
    );
  }

  // sets the table question as the selected question's parentId
  useQuestion(question) {
    this.columnQuestions.push({
      ...question,
      updateParentId: true
    });
    this.updateTableColumnOrder();
    $(this.addQuestionModal.nativeElement).modal("hide");
  }

  // remove question from table
  removeQuestion(question) {
    this.columnQuestions = this.columnQuestions.filter(
      q => q.id !== question.id
    );
    this.updateTableColumnOrder();
  }

  deleteQuestion(question: IQuestionWithStatus) {
    let qUpdateObs = [];
    if (!this.isDeletedQuestion) {
      if (confirm("Are you sure you want to delete this question?")) {
        let questionsNeedDelete = [];
        questionsNeedDelete.push(question);
        if (question.displayFieldType === "table") {
          this.columnQuestions.forEach(cq => questionsNeedDelete.push(cq));
        }
        questionsNeedDelete.forEach(q => {
          qUpdateObs.push(
            this.questionService.updateQuestion({
              id: q.id,
              question: q.question,
              status: 99
            })
          );

          if (
            q.displayFieldType === "drop down" ||
            q.displayFieldType === "radio"
          ) {
            qUpdateObs.push(
              this.dropdownService.updateAllDropdownStatusWithSameQuestionId({
                questionId: q.id,
                status: 99
              })
            );
          }
        });
      }
    } else {
      if (confirm("Are you sure you want to restore this question?")) {
        let questionsNeedRestore = [];
        questionsNeedRestore.push(question);
        if (question.displayFieldType === "table") {
          this.columnQuestions.forEach(cq => questionsNeedRestore.push(cq));
        }
        questionsNeedRestore.forEach(q => {
          qUpdateObs.push(
            this.questionService.updateQuestion({
              id: q.id,
              question: q.question,
              status: 0
            })
          );

          if (
            q.displayFieldType === "drop down" ||
            q.displayFieldType === "radio"
          ) {
            qUpdateObs.push(
              this.dropdownService.updateAllDropdownStatusWithSameQuestionId({
                questionId: q.id,
                status: 0
              })
            );
          }
        });
      }
    }

    forkJoin(qUpdateObs).subscribe(() => {
      this.isDeletedQuestion = !this.isDeletedQuestion;
    });
  }

  save() {
    this.isSaving = true;

    const reqBody = {
      id: this.question.id || null,
      displayFieldType: this.question.displayFieldType,
      isTab: this.question.isTab,
      hidden: this.question.hidden,
      hiddenForPreparer: this.question.hiddenForPreparer,
      complexityOverride: this.question.complexityOverride,
      topLevel: this.question.topLevel,
      question: this.question.question,
      dataType: this.question.dataType,
      mandatory: this.question.mandatory,
      lockMode: this.question.lockMode,
      lockType: this.question.lockType,
      default: this.question.default,
      min: this.question.min,
      max: this.question.max,
      maxLength: this.question.maxLength,
      valueFormat: this.question.valueFormat,
      hint: this.question.hint,
      APSRef: this.question.APSRef,
      APSSection: this.question.APSSection,
      APSSubsection: this.question.APSSubsection,
      APSQuestionGrouping: this.question.APSQuestionGrouping,
      APSDimension: this.question.APSDimension,
      APSOrder: this.question.APSOrder,
      APSCalcField: this.question.APSCalcField,
      tacRef: this.question.tacRef,
      isCurrency: this.question.isCurrency,
      isPercentage: this.question.isPercentage,
      dependantMandatoryParentId: this.question.dependantMandatoryParentId,
      dependantMandatoryParentValue: this.question
        .dependantMandatoryParentValue,
      questionDropdowns: this.question.questionDropdowns,
      year: this.question.year,
      medicareCoordinates: this.question.medicareCoordinates,
      customFilePath: this.question.customFilePath,
      sharepointTags: this.question.sharepointTags,
      transmittalLetterRef: this.question.transmittalLetterRef,
      tag: this.question.tag,
      taxServiceIdentifier: this.question.taxServiceIdentifier,
      isManualAPSEntry: this.question.isManualAPSEntry,
      lodgementBotField: this.question.lodgementBotField,
      alwaysEditable: this.question.alwaysEditable,
      sourceId: this.question.sourceId,
      force: false
    };

    if (this.question.dependantMandatoryParentValue != null)
      reqBody.dependantMandatoryParentValue = String(
        this.question.dependantMandatoryParentValue
      );

    // convert to required data types
    if (this.getQuestionType(this.question) === "number") {
      const parseNumber = x => {
        if (x == null) {
          return null;
        }
        const n = Number(x);
        return Number.isFinite(n) ? n : null;
      };

      reqBody.default =
        reqBody.default != null ? String(reqBody.default) : null;
      reqBody.min = parseNumber(reqBody.min);
      reqBody.max = parseNumber(reqBody.max);
      reqBody.maxLength = parseNumber(reqBody.maxLength);
    } else if (this.getQuestionType(this.question) === "date picker") {
      reqBody.dataType = "date";
    } else if (this.getQuestionType(this.question) === "table") {
      reqBody.dataType = "table";
      // Save chart type if chart is selected
      reqBody.displayFieldType = this.chartDisplayFieldType;
    }
    // tables require checkboxes to be boolean
    else if (this.getQuestionType(this.question) === "checkbox") {
      reqBody.dataType = "boolean";
    } else {
      reqBody.dataType = "string";
    }

    // question dropdowns can cause BE error if too large (dorpdowns are saved separately)
    reqBody.questionDropdowns = [];

    let getTaxReturnStructureObs;
    let qUpdateObs = [];
    const columnQuestionSubGroupAssociations = [];
    const saveColumnQuestions = questionId => {
      const handleColumnQuestions = () => {
        this.columnQuestions.forEach((q: any) => {
          if (q.updateParentId) {
            qUpdateObs.push(
              this.questionService.updateQuestion({
                id: q.id,
                parentId: questionId
              })
            );

            // for existing table q, the column q also needs to be associated to sc and sg of which the tbl question belongs
            if (this.question.id) {
              if (
                subCategory &&
                !subCategory.questions.find(_q => _q.id === q.id)
              ) {
                qUpdateObs.push(
                  this.subcategoryService.createSubcategoryQuestionAssociation(
                    subCategory.id,
                    {
                      questionId: q.id
                    }
                  )
                );
              }

              if (subGroup && !subGroup.questions.find(_q => _q.id === q.id)) {
                columnQuestionSubGroupAssociations.push(
                  this.subgroupService.addSubGroupQuestionAssociation(
                    subGroup.id,
                    {
                      questionId: q.id
                    }
                  )
                );
              }
            }
          }
        });
      };

      // for existing table q, the column q also needs to be associated to sc and sg of which the tbl question belongs
      let subCategory;
      let subGroup;
      if (this.question.id) {
        getTaxReturnStructureObs = this.taxReturnService
          // we may need to get all of the structures as the taxReturnTypeId may not have been set yet to the table question
          .getTaxReturnStructure(
            this.question.year,
            this.question.taxReturnTypeId || -1
          )
          .pipe(
            map((taxStructure: any) => {
              taxStructure.forEach(c => {
                c.subcategories.forEach(sc => {
                  if (sc.questions.find(_q => _q.id === this.question.id)) {
                    subCategory = sc;
                    sc.subgroups.forEach(sg => {
                      if (sg.questions.find(_q => _q.id === this.question.id))
                        subGroup = sg;
                    });
                  }
                });
              });

              handleColumnQuestions();
            })
          );
      } else {
        handleColumnQuestions();
      }
    };

    const updateDropdown = questionId => {
      if (this.question.questionDropdowns) {
        this.question.questionDropdowns.forEach(qD => {
          if (qD.id && qD.id >= 0) {
            const originalDp = this.originalQuestion.questionDropdowns.find(
              _qD => _qD.id === qD.id
            );
            const hasChanged =
              qD.value !== originalDp.value ||
              qD.display !== originalDp.display ||
              qD.order !== originalDp.order ||
              qD.selected !== originalDp.selected ||
              qD.disabled !== originalDp.disabled ||
              qD.status !== originalDp.status;

            if (hasChanged)
              qUpdateObs.push(
                this.dropdownService.updateDropdown({
                  id: qD.id,
                  value: qD.value || qD.display || "",
                  display: qD.display,
                  order: qD.order,
                  selected: qD.selected,
                  disabled: qD.disabled,
                  questionId: questionId,
                  status: qD.status
                })
              );
          } else if (qD.value || qD.display) {
            qUpdateObs.push(
              this.dropdownService.addDropdown({
                value: qD.value || qD.display,
                display: qD.display,
                order: qD.order,
                selected: qD.selected,
                disabled: qD.disabled,
                questionId: questionId,
                status: qD.status
              })
            );
          }
        });
      }
    };

    // for new question, need to get q id before saving table questions
    if (!this.question.id) {
      this.questionService.addQuestion(reqBody).subscribe(
        (q: IFormField) => {
          if (q.dataType === "table") saveColumnQuestions(q.id);
          if (
            q.displayFieldType === "drop down" ||
            q.displayFieldType === "radio"
          )
            updateDropdown(q.id);

          if (qUpdateObs.length > 0) {
            forkJoin(qUpdateObs).subscribe(
              () => {
                if (columnQuestionSubGroupAssociations.length > 0) {
                  forkJoin(
                    columnQuestionSubGroupAssociations
                  ).subscribe(
                    () => {
                      // refresh route to allow new question id to appear in url
                      this.router.navigate(["settings", "questions", q.id]);
                      this.isSaving = false;
                    },
                    () => (this.isSaving = false)
                  );
                } else {
                  this.router.navigate(["settings", "questions", q.id]);
                  this.isSaving = false;
                }
              },
              () => (this.isSaving = false)
            );
          } else {
            this.router.navigate(["settings", "questions", q.id]);
            this.isSaving = false;
          }
        },
        () => (this.isSaving = false)
      );
    }

    // existing question
    else {
      if (this.question.dataType === "table")
        saveColumnQuestions(this.question.id);
      if (
        this.question.displayFieldType === "drop down" ||
        this.question.displayFieldType === "radio"
      )
        updateDropdown(this.question.id);
      if ( this.question.lockMode === "auto" && this.originalQuestion.lockMode === "manual" ) {
        if(this.question.answersCount > 0){
          if (confirm(this.questionHasAnswerText)) {
          reqBody.force = true;
          } else {
            this.isSaving = false;
            return;
          }
        } else{
          reqBody.force = true;
        }
      }
      let savedQ;
      qUpdateObs.push(
        this.questionService
          .updateQuestion(reqBody)
          .pipe(map((q: Question) => (savedQ = q)))
      );

      const handleUpdateObs = () => {
        forkJoin(qUpdateObs).subscribe(
          () => {
            if (columnQuestionSubGroupAssociations.length > 0) {
              forkJoin(columnQuestionSubGroupAssociations).subscribe(
                () => {
                  this.questionService
                    .getquestionById(this.question.id)
                    .subscribe((data: IFormField) => {
                      this.initQuestion(data);
                      this.isSaving = false;
                    });
                },
                () => (this.isSaving = false)
              );
            } else {
              this.questionService
                .getquestionById(this.question.id)
                .subscribe((data: IFormField) => {
                  this.initQuestion(data);
                  this.isSaving = false;
                });
            }
          },
          () => (this.isSaving = false)
        );
      };

      if (getTaxReturnStructureObs) {
        forkJoin(getTaxReturnStructureObs).subscribe(() => {
          handleUpdateObs();
        });
      } else {
        handleUpdateObs();
      }
    }
  }

  omitSpecialCharInCustomFilePath(event) {
    var k;
    k = event.charCode; //         k = event.keyCode;  (Both can be used)
    // Exclude these chars "!@#$%^&*()+=`~/,;:'][}{<>?|
    return (
      !(k > 32 && k < 45) &&
      !(k > 57 && k < 65) &&
      k !== 47 &&
      k !== 91 &&
      k !== 93 &&
      k !== 94 &&
      k !== 96 &&
      !(k > 122 && k < 127)
    );
  }

  syncCache() {
    this.isSyncingCache = true;
    this.taxReturnService.syncTaxReturnStructure().subscribe(
      () => {
        this.isSyncingCache = false;
      },
      () => {
        this.isSyncingCache = false;
      }
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
